import { useEffect, useRef } from 'react';
import { Outlet, useNavigation } from 'react-router-dom';
import LoadingBar, { type LoadingBarRef } from 'react-top-loading-bar';
import { useLocalStorage } from 'usehooks-ts';

import { SidebarInset, SidebarProvider } from '@kognitos/lattice';
import { AppErrorBoundary } from '@/modules/sentry';
import { useAuth } from '@/shared/auth';
import { STORAGE_KEYS } from '@/shared/constants';

import { useIsOnboardingOrInvitationPage } from '@/routes/util';
import { useSyncUrlParamsToStore } from '@/shared/hooks/useSyncUrlParamsToStore';
import { AppSidebar } from './AppSidebar';

import '@/modules/automation/styles.css';
import noop from 'lodash/noop';

const Layout = () => {
  useSyncUrlParamsToStore();
  const { isAuthenticated } = useAuth();
  const navigation = useNavigation();
  const loadingBarRef = useRef<LoadingBarRef>(null);
  const isOnboardingOrInvitationPage = useIsOnboardingOrInvitationPage(); // Detect onboarding to keep sidebar collapsed

  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage(
    STORAGE_KEYS.SIDEBAR_COLLAPSED,
    false,
  );

  const handleSidebarChange = (open: boolean) => {
    setSidebarCollapsed(!open);
  };

  // eslint-disable-next-line no-restricted-syntax -- reason: Sync navigation state to loading bar imperative API
  useEffect(() => {
    if (navigation.state === 'loading') {
      loadingBarRef.current?.continuousStart();
    } else {
      loadingBarRef.current?.complete();
    }
  }, [navigation.state]);

  return (
    <>
      <LoadingBar ref={loadingBarRef} height={3} color="var(--primary)" />
      <SidebarProvider
        open={isOnboardingOrInvitationPage ? false : !sidebarCollapsed} // Force closed during onboarding/invitation
        onOpenChange={isOnboardingOrInvitationPage ? noop : handleSidebarChange} // Ignore toggle attempts during onboarding/invitation
      >
        {isAuthenticated && <AppSidebar />}
        <SidebarInset>
          <div className="flex flex-col min-h-screen">
            <main className="block flex-1">
              <AppErrorBoundary>
                <Outlet />
              </AppErrorBoundary>
            </main>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </>
  );
};

export default Layout;
